import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faUser,
  faTag,
  faClock,
  faArrowLeft,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug;

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    if (slug) {
      fetchArticleBySlug(slug);
    } else {
      setCurrentArticle(null);
    }
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArticles = async () => {
    setLoading(true);
    try {
      // Get all published articles
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (error) throw error;

      setArticles(data || []);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(data.map(article => article.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchArticleBySlug = async (slug) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .single();

      if (error) throw error;

      setCurrentArticle(data);
    } catch (error) {
      console.error("Error fetching article:", error);
      setError("Failed to load article");
      navigate("/articole", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ro-RO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category === selectedCategory ? "" : category);
  };

  // Filter articles based on search term and category
  const filteredArticles = articles.filter((article) => {
    const matchesSearch = 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.excerpt && article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (article.content && article.content.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory ? article.category === selectedCategory : true;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="articles-page">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Se încarcă articolele...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="articles-page">
        <div className="container py-5">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Display a single article
  if (currentArticle) {
    return (
      <div className="articles-page">
        <section className="article-hero">
          <div className="article-overlay"></div>
          <div className="container">
            <div className="article-hero-content">
              <h1>{currentArticle.title}</h1>
              <p>{currentArticle.excerpt}</p>
            </div>
          </div>
        </section>

        <div className="container py-5">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="article-meta mb-4">
                {currentArticle.author && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    {currentArticle.author}
                  </span>
                )}
                {currentArticle.published_at && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faCalendar} className="me-2" />
                    {formatDate(currentArticle.published_at)}
                  </span>
                )}
                {currentArticle.reading_time_minutes && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    {currentArticle.reading_time_minutes} minute de citire
                  </span>
                )}
                {currentArticle.category && (
                  <span className="badge bg-primary me-2">
                    {currentArticle.category}
                  </span>
                )}
              </div>

              {currentArticle.featured_image_url && (
                <div className="article-featured-image mb-4">
                  <img
                    src={currentArticle.featured_image_url}
                    alt={currentArticle.title}
                    className="img-fluid rounded shadow"
                  />
                </div>
              )}

              <div className="article-content mb-5">
                <div dangerouslySetInnerHTML={{ __html: currentArticle.content }} />
              </div>

              {currentArticle.tags && currentArticle.tags.length > 0 && (
                <div className="article-tags mb-4">
                  <FontAwesomeIcon icon={faTag} className="me-2" />
                  {currentArticle.tags.map((tag, index) => (
                    <span key={index} className="badge bg-light text-dark me-2">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="article-navigation">
                <Link to="/articole" className="btn btn-outline-primary">
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Înapoi la articole
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Display the list of articles
  return (
    <div className="articles-page">
      <section className="article-hero">
        <div className="article-overlay"></div>
        <div className="container">
          <div className="article-hero-content">
            <h1>Articole medicale</h1>
            <p>Informații medicale și sfaturi pentru sănătatea dumneavoastră</p>
          </div>
        </div>
      </section>

      <div className="container py-5">
        <div className="row">
          <div className="col-lg-8">
            <div className="articles-list">
              <h2 className="mb-4">Articole recente</h2>
              
              {filteredArticles.length === 0 ? (
                <div className="alert alert-info">
                  Nu au fost găsite articole care să corespundă criteriilor de căutare.
                </div>
              ) : (
                filteredArticles.map((article) => (
                  <div key={article.id} className="article-card mb-4">
                    <div className="row g-0">
                      {article.featured_image_url && (
                        <div className="col-md-4">
                          <img
                            src={article.featured_image_url}
                            alt={article.title}
                            className="img-fluid rounded-start h-100 object-fit-cover"
                            style={{ maxHeight: "250px" }}
                          />
                        </div>
                      )}
                      <div className={article.featured_image_url ? "col-md-8" : "col-md-12"}>
                        <div className="card-body">
                          <h3 className="card-title">
                            <Link to={`/articole/${article.slug}`} className="text-decoration-none">
                              {article.title}
                            </Link>
                          </h3>
                          <div className="article-meta mb-2">
                            {article.author && (
                              <span className="me-3 small text-muted">
                                <FontAwesomeIcon icon={faUser} className="me-1" />
                                {article.author}
                              </span>
                            )}
                            {article.published_at && (
                              <span className="me-3 small text-muted">
                                <FontAwesomeIcon icon={faCalendar} className="me-1" />
                                {formatDate(article.published_at)}
                              </span>
                            )}
                            {article.reading_time_minutes && (
                              <span className="small text-muted">
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                {article.reading_time_minutes} min
                              </span>
                            )}
                          </div>
                          <p className="card-text">{article.excerpt}</p>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              {article.category && (
                                <span className="badge bg-primary me-2">
                                  {article.category}
                                </span>
                              )}
                              {article.is_featured && (
                                <span className="badge bg-warning text-dark">
                                  Recomandat
                                </span>
                              )}
                            </div>
                            <Link
                              to={`/articole/${article.slug}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              Citește mai mult
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div className="col-lg-4">
            <div className="articles-sidebar">
              <div className="search-box mb-4">
                <h4>Caută articole</h4>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Caută..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                </div>
              </div>
              
              <div className="categories-box mb-4">
                <h4>Categorii</h4>
                <div className="list-group">
                  {categories.map((category) => (
                    <button
                      key={category}
                      className={`list-group-item list-group-item-action ${
                        selectedCategory === category ? "active" : ""
                      }`}
                      onClick={() => handleCategoryFilter(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
              
              {articles.filter(article => article.is_featured).length > 0 && (
                <div className="featured-box">
                  <h4>Articole recomandate</h4>
                  <div className="list-group">
                    {articles
                      .filter(article => article.is_featured)
                      .slice(0, 3)
                      .map((article) => (
                        <Link
                          key={article.id}
                          to={`/articole/${article.slug}`}
                          className="list-group-item list-group-item-action"
                        >
                          <div className="d-flex w-100 justify-content-between">
                            <h5 className="mb-1">{article.title}</h5>
                          </div>
                          <small className="text-muted">
                            {formatDate(article.published_at)}
                          </small>
                        </Link>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Articles;
